import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { uomTypesService } from "../services/svc_uomTypes";
import { uomUnitsService } from "../services/svc_uomUnits";
import { queryKeys } from "../lib/queryKeys";
import { ListRequest } from "../types/common/commonTypes";

export const useTSQUOMTypeList = (params: ListRequest) => {
  return useQuery({
    queryKey: queryKeys.uomtype.list(params),
    queryFn: () => uomTypesService.list(params),
    placeholderData: keepPreviousData,
  });
};

export const useTSQUOMTypeId = (id: string) => {
  return useQuery({
    queryKey: queryKeys.uomtype.detail(id),
    queryFn: () => {
      return uomTypesService.getById(id);
    },
    enabled: !!id,
  });
};

export const useTSMCreateUOMType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uomTypesService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.uomtype.lists() });
    },
    onError: (error) => {
      console.error("Failed to create UOM Type:", error);
    },
  });
};

export const useTSMUpdateUOMType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      uomTypesService.update(id, data),
    onSuccess: (updatedUOMType) => {
      queryClient.setQueryData(
        queryKeys.uomtype.detail(updatedUOMType.id),
        updatedUOMType,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.uomtype.lists() });
    },
    onError: (error) => {
      console.error("Failed to update UOM Type:", error);
    },
  });
};

export const useTSMDeactivateUOMType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uomTypesService.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.uomtype.lists() });
    },
    onError: (error) => {
      console.error("Failed to deactivate UOM Type:", error);
    },
  });
};

export const useTSMReactivateUOMType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uomTypesService.reactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.uomtype.lists() });
    },
    onError: (error) => {
      console.error("Failed to restore UOM Type:", error);
    },
  });
};

export const useTSQUOMUnitList = (
  params: ListRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.uomunit.list(params),
    queryFn: () => uomUnitsService.list(params),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQUOMUnitId = (id: string) => {
  return useQuery({
    queryKey: queryKeys.uomunit.detail(id),
    queryFn: () => uomUnitsService.getById(id),
    enabled: !!id,
  });
};

export const useTSMCreateUOMUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uomUnitsService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.uomunit.lists() });
    },
    onError: (error) => {
      console.error("Failed to create UOM Unit:", error);
    },
  });
};

export const useTSMUpdateUOMUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      uomUnitsService.update(id, data),
    onSuccess: (updatedUOMUnit) => {
      queryClient.setQueryData(
        queryKeys.uomunit.detail(updatedUOMUnit.id),
        updatedUOMUnit,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.uomunit.lists() });
    },
    onError: (error) => {
      console.error("Failed to update UOM Unit:", error);
    },
  });
};
